import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import { Pill, Progress, Loader, Empty } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const CATS = ["all", "policy", "environment", "education", "health", "civic", "rights"];

const Petitions = () => {
  const { user, login } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get("/petitions", { params: { category: cat, q: q || undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [cat, q]);

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16" data-testid="petitions-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Pressure points</span>
          <h1 className="mt-3 font-bebas text-5xl sm:text-6xl tracking-wide uppercase">Petitions</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-white/10 px-3 h-11 bg-white/[0.03] focus-within:border-neon/60 transition-colors">
            <Search size={15} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search petitions" data-testid="petitions-search" className="bg-transparent outline-none text-sm w-52 placeholder:text-white/30" />
          </div>
          {user ? (
            <Link to="/petitions/new" data-testid="start-petition-btn" className="bg-neon px-6 h-11 grid place-items-center text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow duration-200">
              Start a petition
            </Link>
          ) : (
            <button onClick={login} data-testid="petitions-login-btn" className="border border-white/20 px-6 h-11 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
              Sign in to start one
            </button>
          )}
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button
            key={c}
            onClick={() => setCat(c)}
            data-testid={`petitions-filter-${c}`}
            className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors duration-200 ${
              cat === c ? "border-neon text-neon bg-neon/10" : "border-white/12 text-white/50 hover:text-white"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="mt-12">
          <Empty text="No petitions here yet. Be the first to draft one." testid="petitions-empty" />
        </div>
      ) : (
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {items.map((p, i) => (
            <Link
              key={p.petition_id}
              to={`/petitions/${p.petition_id}`}
              data-testid={`petition-card-${i}`}
              className="group border border-white/10 bg-ink-surface p-7 hover:border-neon/50 hover:-translate-y-1 transition-[transform,border-color] duration-300"
            >
              <div className="flex gap-2">
                <Pill tone="blue">{p.category}</Pill>
                {p.deadline && <Pill tone="yellow">by {new Date(p.deadline).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</Pill>}
              </div>
              <h3 className="mt-5 font-outfit text-xl font-bold leading-snug group-hover:text-neon transition-colors">{p.title}</h3>
              <p className="mt-3 text-sm text-white/50 line-clamp-3">{p.summary}</p>
              <div className="mt-7">
                <div className="flex items-end justify-between mb-3">
                  <span className="font-bebas text-3xl tracking-wide">{p.signature_count}</span>
                  <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">of {p.goal}</span>
                </div>
                <Progress value={p.signature_count} goal={p.goal} />
              </div>
              <div className="mt-5 text-[11px] uppercase tracking-[0.18em] text-white/35">Target · {p.target_authority}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default Petitions;
